import React, {useState, useRef, useEffect} from 'react';
import { connect } from 'react-redux';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
    Flex, Box, Heading, FormControl, FormLabel, Input, Button, ButtonGroup, Text, CircularProgress,
    InputGroup, InputRightElement, InputRightAddon, HStack, Wrap, WrapItem, Center, VStack, useToast
} from '@chakra-ui/react';
import { API_SUB_URL_PATIENT, PATIENT_INFORMATION_MAX_AGE, ERROR_CODES } from '../../../constants';
import NoEventPage from '../../../components/NoEvent';
import { displayConfirmAlert } from '../../utils';

function RegisteredPatient ({ 
    selectedClinic, scheduleDate, selectedMedicalTime, reasonNeedDoctor, t
}) {
    const [ cookies, setCookie, removeCookie ] = useCookies(["selectedClinic", "makeDoctorAppointmentDatas"]);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ searchText, setSearchText ] = useState(null);
    const [ patientDetail, setPatientDetail ] = useState(null);

    const navigate = useNavigate();
    const toast = useToast();
    const mounted = useRef();

    useEffect(() => {
        if(!mounted.current) {
            if(!cookies.makeDoctorAppointmentDatas?.data?.isNewRegister) {
                setPatientDetail(cookies.makeDoctorAppointmentDatas?.data?.patientDetail);
            }
            mounted.current = true;
        }
    });

    const handleSearch = async() => {
        setIsLoading(true);

        try
        {
            const getPatientDetailRes = await axios.get(`${process.env.REACT_APP_API_ENDPOINT_URL}${API_SUB_URL_PATIENT}?company_id=${cookies.selectedClinic?.data?.id}&&searchText=${searchText}`, {
                auth: {
                    username: process.env.REACT_APP_BASIC_AUTH_USERNAME,
                    password: process.env.REACT_APP_BASIC_AUTH_PASSWORD
                }
            });
            
            if(getPatientDetailRes?.data?.result?.length > 0) {
                setPatientDetail(getPatientDetailRes?.data?.result[0]);
            }
            else {
                displayConfirmAlert(`Hệ thống không tìm thấy thông tin '${searchText}' mà bạn vừa nhập!`);
                setPatientDetail(null);
            }
        }
        catch(error) {
            if(error?.code) {
                toast({
                    description: `${ERROR_CODES[error?.code]} (${error?.code})`,
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                    position: "bottom-right"
                });
            }
        }

        setIsLoading(false);
    }

    const handleSubmit = () => {
        if(!selectedClinic) {
            toast({
                description: "Bạn chưa chọn Phòng khám!",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "bottom-right"
            });
        } else if(!scheduleDate) {
            toast({
                description: "Bạn chưa chọn Ngày khám!",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "bottom-right"
            });
        } else if(!selectedMedicalTime) {
            toast({
                description: "Bạn chưa chọn Giờ khám!",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "bottom-right"
            });
        } else if(!reasonNeedDoctor || reasonNeedDoctor?.length === 0) {
            toast({
                description: "Bạn chưa nhập lý do cần khám!",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "bottom-right"
            });
        } else {
            setCookie("makeDoctorAppointmentDatas", 
                { 
                    data: { 
                        isNewRegister: false,
                        company_id: cookies.selectedClinic?.data?.id,
                        scheduleDate, 
                        selectedMedicalTime, 
                        reasonNeedDoctor,
                        patientDetail,
                        //...patientDetail,
                    } 
                }, 
                { 
                    path: '/', maxAge: PATIENT_INFORMATION_MAX_AGE
                }
            );
            navigate("/department");
        }
    }

    const { name, yearOfBirth, gender, address, passport, phone, email, social_insurance } = patientDetail || {};

    if(!(cookies.selectedClinic?.data?.id)) {
        return (
            <React.Fragment>
                <NoEventPage text="Bạn vui lòng chọn phòng khám trước khi tiếp tục!"/>
            </React.Fragment>
        )
    }

    return (
        <VStack height="100%" mx={{ base: 0, md: 0, lg: 0 }} my={4} justify="center">
            <InputGroup maxW="500px">
                <Input type='text' placeholder={t("Type_Passport_Or_Phone")}
                    onChange={(event) => setSearchText(event.target.value)}
                />

                <InputRightAddon p={0}> 
                    <Button colorScheme='purple' borderTopLeftRadius={0} borderBottomLeftRadius={0}
                        isDisabled={!searchText || searchText?.length === 0}
                        isLoading={isLoading}
                        loadingText="Đang tra cứu"
                        onClick={() => {
                            handleSearch();
                        }
                    }>
                       {t("Search")}
                    </Button>
                </InputRightAddon>
            </InputGroup>

            {
                patientDetail && 
                <React.Fragment>
                    <Flex flexDirection={{ base: "column", md: "row", lg: "row" }} w="100%" justify={{ base: "flex-start", md: "center", lg: "center" }}
                        px={2} py={2} marginTop="10px" gap={5} fontSize="20px" 
                    >
                        <Flex flexDirection="column" minW="200px" maxW="400px">
                            <HStack>
                                <Text fontWeight="400">{t("Full_Name")}</Text>
                                <Text fontWeight="600" color="#ff9800">{name || ""}</Text>
                            </HStack>

                            <HStack>
                                <Text fontWeight="400">{t("Year_Of_Birth")}</Text>
                                <Text fontWeight="600">{yearOfBirth || ""}</Text>
                            </HStack>

                            <HStack>
                                <Text fontWeight="400">{t("Gender")}</Text>
                                <Text fontWeight="600">{gender || ""}</Text>
                            </HStack>

                            <HStack>
                                <Text fontWeight="400" whiteSpace="nowrap">{t("Address")}</Text>
                                <Text fontWeight="600">{address || ""}</Text>
                            </HStack>
                        </Flex>
                            
                        <Flex flexDirection="column" minW="200px" maxW="400px">
                            <HStack>
                                <Text fontWeight="400">{t("Passport")}</Text>
                                <Text fontWeight="600">{passport || ""}</Text>
                            </HStack>

                            <HStack>
                                <Text fontWeight="400">{t("Phone")}</Text>
                                <Text fontWeight="600">{phone || ""}</Text>
                            </HStack>

                            <HStack>
                                <Text fontWeight="400">{t("Email")}</Text>
                                <Text fontWeight="600">{email || ""}</Text>
                            </HStack>

                            <HStack>
                                <Text fontWeight="400">{t("Medical_Card")}</Text>
                                <Text fontWeight="600">{social_insurance || ""}</Text>
                            </HStack>
                        </Flex>
                    </Flex>
                    
                    <Center marginTop="10px">
                        <Button colorScheme='green' width="300px" height="70px" isDisabled={!patientDetail}
                            onClick={() => handleSubmit()}
                        >
                            <Text fontSize="18px" fontWeight="700">{t("Confirm")}</Text>
                        </Button>
                    </Center>
                </React.Fragment>
            }
        </VStack>
    )
}

export default RegisteredPatient;